import React, { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';

import { IEnvironmentalCredential } from '../../greenTypes';
import { formatValueAsString } from '../../../../utils/helpers';
import { ColDef, themeBalham } from 'ag-grid-enterprise';
import ImoAndNameCellRenderer from '../../../../shared/components/renderers/customCellRenderers/ImoAndNameCellRenderer';

interface IVesselsDetails {
	data?: IEnvironmentalCredential[];
	year?: number;
}

const VesselsDetails: React.FC<IVesselsDetails> = ({ ...props }) => {
	function transformVesselData(data: IEnvironmentalCredential[]) {
		return data.map((vessel) => {
			const ciiYearly: Record<number, string> = {};
			vessel.ciiValues.forEach((cii) => {
				ciiYearly[cii.year] = cii.currentCii ?? '-';
			});
			return {
				...vessel,
				...ciiYearly, // Spread the year-wise currentCii values as flat properties
			};
		});
	}

	function getCiiYears(data: IEnvironmentalCredential[]) {
		const yearSet = new Set<number>();
		data.forEach((vessel) => {
			vessel.ciiValues.forEach((cii) => {
				yearSet.add(cii.year);
			});
		});
		return Array.from(yearSet).sort(); // Sort for consistent column order
	}

	function generateCiiColumns(years: number[]) {
		return {
			headerName: 'Estimated CII Ratings, (Forecasts Based on Current CII Score)',
			children: years.map((year) => ({
				headerName: year.toString(),
				field: year.toString(),
				floatingFilter: true,
				filter: 'agSetColumnFilter',
				valueFormatter: (params: any) => params.value || '-',
			})),
		};
	}

	const transformedData = transformVesselData(props.data || []);
	const ciiYears = getCiiYears(props.data || []);
	const ciiColumns = generateCiiColumns(ciiYears);

	const columnDefs = useMemo<ColDef[]>(
		() => [
			{
				headerName: 'Vessel Details',
				children: [
					{
						headerName: 'Name',
						field: 'vesselName',
						headerTooltip: 'Vessel Name',
						floatingFilter: true,
						cellRenderer: ImoAndNameCellRenderer,
						cellRendererParams: {
							// This is where you can pass additional parameters to the cell renderer if needed
							imoNumber: (params: any) => params.data.imoNumber,
						},
					},
					// { headerName: 'IMO Number', field: 'imoNumber' },
					{
						headerName: 'IMO Number',
						field: 'imoNumber',
						floatingFilter: true,
						headerTooltip: 'IMO Number',
						cellRenderer: ImoAndNameCellRenderer,
						cellRendererParams: {
							// This is where you can pass additional parameters to the cell renderer if needed
							imoNumber: (params: any) => params.data.imoNumber,
						},
					},
					{ headerName: 'Sector', field: 'sector', floatingFilter: true, filter: 'agSetColumnFilter' },
					{
						headerName: 'Size',
						field: 'size',
						floatingFilter: true,
						valueFormatter: (params: any) => `${formatValueAsString(params.data?.size)}`,
					},
					{ headerName: 'Year of Delivery', field: 'yearOfDelivery', floatingFilter: true },
				],
			},
			{
				headerName: `Current Estimate`,
				children: [
					{
						headerName: 'AER / cgDIST (TtW)',
						field: 'estimatedAERTtW',
						floatingFilter: true,
						valueFormatter: (params: any) => formatValueAsString(params.value, { maximumFractionDigits: 1 }, '-', '', ''),
					},
					{
						headerName: 'AERe / cgDISTe (WtW)',
						field: 'estimatedAERWtW',
						floatingFilter: true,
						valueFormatter: (params: any) => formatValueAsString(params.value, { maximumFractionDigits: 1 }, '-', '', ''),
					},
					{
						headerName: 'CII Score',
						field: 'ciiScore',
						floatingFilter: true,
						valueFormatter: (params: any) => formatValueAsString(params.value, { maximumFractionDigits: 1 }, '-', '', ''),
					},
				],
			},
			{
				headerName: `Trajectory Calculations (${props.year})`,
				children: [
					{
						headerName: 'Vessel Weighting (Loan Balance $ Mn)',
						field: 'vesselWeighting',
						headerTooltip: 'Vessel Weighting (Loan Balance $ Mn)',
						floatingFilter: true,
						valueFormatter: (params: any) =>
							`${formatValueAsString(params.data?.vesselWeighting, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
					},
					{
						headerName: 'Required Carbon Intensity (PP Trajectory: Minimum)',
						field: 'requiredCarbonIntensityPpTrajectoryMinimum',
						headerTooltip: 'Required Carbon Intensity (PP Trajectory: Minimum)',
						floatingFilter: true,
						valueFormatter: (params: any) =>
							`${formatValueAsString(params.data?.requiredCarbonIntensityPpTrajectoryMinimum, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
					},
					{
						headerName: 'Climate Alignment (PP Trajectory: Minimum)',
						field: 'climateAlignmentPpTrajectoryMinimum',
						headerTooltip: 'Climate Alignment (PP Trajectory: Minimum)',
						floatingFilter: true,
						valueFormatter: (params: any) =>
							`${formatValueAsString(params.data?.climateAlignmentPpTrajectoryMinimum, { minimumFractionDigits: 0, maximumFractionDigits: 2 }, '', '', '%')}`,
					},
					{
						headerName: 'Required Carbon Intensity (PP Trajectory: Striving)',
						field: 'requiredCarbonIntensityPpTrajectoryStriving',
						headerTooltip: 'Required Carbon Intensity (PP Trajectory: Striving)',
						floatingFilter: true,
						valueFormatter: (params: any) =>
							`${formatValueAsString(params.data?.requiredCarbonIntensityPpTrajectoryStriving, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
					},
					{
						headerName: 'Climate Alignment (PP Trajectory: Striving)',
						field: 'climateAlignmentPpTrajectoryStriving',
						headerTooltip: 'Climate Alignment (PP Trajectory: Striving)',
						floatingFilter: true,
						valueFormatter: (params: any) =>
							`${formatValueAsString(params.data?.climateAlignmentPpTrajectoryStriving, { minimumFractionDigits: 0, maximumFractionDigits: 2 }, '', '', '%')}`,
					},
				],
			},
		],
		[props.year],
	);

	// 2. Insert CII columns after "Current Estimate" section (which is at index 1)
	const indexToInsert = 2; // index after 'Current Estimate'

	const finalColumnDefs = [...columnDefs.slice(0, indexToInsert), ciiColumns, ...columnDefs.slice(indexToInsert)];
	const defaultColDef = {
		resizable: true,
		sortable: true,
		filter: 'agTextColumnFilter',
	};

	return (
		<div className="w-full h-full">
			<AgGridReact columnDefs={finalColumnDefs} rowData={transformedData} defaultColDef={defaultColDef} theme={themeBalham} />
		</div>
	);
};

export default VesselsDetails;
